import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FormReviewDashboardService } from './form-review-dashboard.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-form-review-dashboard',
    templateUrl: './form-review-dashboard.component.html',
    styleUrls: ['./form-review-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormReviewDashboardComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  loadingList = false;
  loadingForms = false;
  expandPdfView = true;
  flatData: any[] = [];
  formsPerEntity: any[] = [];
  pdfs: any[] = [];
  selectedEntity: any = null;
  selectedForm: any = null;
  isSelectAll = false;
  searchTerm = '';
  formSearchTerm = '';
  recordsCount = 0;
  showBSLAFilter = false;

  filter: any = {
    category: 'C',
    location: 'D',
    lEType: 'HO'
  };

  filterCatMapping: any = { 'C': 'Corporation', 'P': 'Partnership' };
  filterLocMapping: any = { 'F': 'Foreign', 'D': 'Domestic' };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private formReviewService: FormReviewDashboardService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    const clientName = this.USER_SETTINGS?.user?.client?.client_name || '';
    this.showBSLAFilter = clientName.toUpperCase().includes('AON');
    this.getEntities();
  }

  getEntities(initialize = false) {
    this.loadingList = true;
    this.cdr.detectChanges();

    const pdfEngineUrl = this.GENERAL_CONFIG.pdf_engine_api_url || this.baseURLs.pdfEngine;
    const srcSystemFlag = this.filter.location;

    this.formReviewService.getViewForDropdown(
      pdfEngineUrl,
      this.globalService?.tax_year || this.filterParams?.tax_year,
      this.globalService?.scenario || this.filterParams?.scenario,
      250,
      this.filter.lEType,
      srcSystemFlag,
      this.filter.category
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.flatData = data || [];
        this.recordsCount = this.flatData.length;
        this.loadingList = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading entities:', error);
        this.loadingList = false;
        this.cdr.detectChanges();
      }
    );
  }

  selectEntity(entity: any) {
    this.selectedEntity = entity;
    this.loadForms(entity);
  }

  loadForms(entity: any) {
    this.loadingForms = true;
    this.cdr.detectChanges();

    const pdfEngineUrl = this.GENERAL_CONFIG.pdf_engine_api_url || this.baseURLs.pdfEngine;

    this.formReviewService.getTaxForms(
      pdfEngineUrl,
      this.globalService?.tax_year || this.filterParams?.tax_year,
      this.globalService?.scenario || this.filterParams?.scenario,
      250,
      entity.LEID || entity.selectedView,
      entity.REPORTING_PERIOD || '1',
      this.filter.lEType,
      entity.CDR_NO || '',
      this.filter.location,
      this.filter.category
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.formsPerEntity = data || [];
        this.loadingForms = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading forms:', error);
        this.loadingForms = false;
        this.cdr.detectChanges();
      }
    );
  }

  catChanged(category: string) {
    this.filter.category = category;
    this.selectedEntity = null;
    this.selectedForm = null;
    this.getEntities();
  }

  locChanged(location: string) {
    this.filter.location = location;
    this.selectedEntity = null;
    this.selectedForm = null;
    this.getEntities();
  }

  leTypeChanged(leType: string) {
    this.filter.lEType = leType;
    this.selectedEntity = null;
    this.selectedForm = null;
    this.getEntities();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
